import { useRef, useState } from 'react';
import { uploadImage } from '@/utils/imageUpload';

const ToolbarButton = ({ onClick, active, title, children }: any) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onClick?.(); }}
    title={title}
    className={`px-2 py-1 rounded text-sm hover:bg-gray-200 transition-colors ${active ? 'bg-gray-300 font-bold' : ''}`}
  >
    {children}
  </button>
);

export default function EditorToolbar({ editor }: { editor: any }) {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [fontSizeOpen, setFontSizeOpen] = useState(false);

  if (!editor) return null;

  const setLink = () => {
    const url = window.prompt('URL', editor.getAttributes('link').href);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const result = await uploadImage(file, '/api/upload/recipe-cover');
      if (result.url) editor.chain().focus().setImage({ src: result.url }).run();
    } catch {
      // upload failure handled by uploadImage utility
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b bg-gray-50">
      <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold"><b>B</b></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic"><em>I</em></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline"><u>U</u></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough"><s>S</s></ToolbarButton>

      <div className="relative">
        <button type="button" onClick={() => setFontSizeOpen(!fontSizeOpen)}
          className="px-2 py-1 text-sm border rounded bg-white hover:bg-gray-200 min-w-[60px] text-left">
          Size ▾
        </button>
        {fontSizeOpen && (
          <div className="absolute top-full left-0 z-50 bg-white border rounded shadow-lg max-h-48 overflow-y-auto">
            {[10,12,14,16,18,20,24,28,32,36,48,64].map(size => (
              <button key={size} type="button"
                onMouseDown={e => { e.preventDefault(); editor.chain().focus().setFontSize(`${size}px`).run(); setFontSizeOpen(false); }}
                className="block w-full text-left px-4 py-1 text-sm hover:bg-gray-100">
                {size}
              </button>
            ))}
          </div>
        )}
      </div>

      <span className="w-px bg-gray-300 mx-1" />

      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1">H1</ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2">H2</ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3">H3</ToolbarButton>

      <span className="w-px bg-gray-300 mx-1" />

      <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List">• List</ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered List">1. List</ToolbarButton>

      <span className="w-px bg-gray-300 mx-1" />

      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align Left">⬅</ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align Center">↔</ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align Right">➡</ToolbarButton>

      <span className="w-px bg-gray-300 mx-1" />

      <ToolbarButton onClick={setLink} active={editor.isActive('link')} title="Link">🔗</ToolbarButton>
      <ToolbarButton onClick={() => imageInputRef.current?.click()} title="Insert Image">
        {isUploading ? '⏳' : '🖼️'}
      </ToolbarButton>
      <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />

      <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote">"</ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code Block">{`</>`}</ToolbarButton>

      <span className="w-px bg-gray-300 mx-1" />

      <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Undo">↩</ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Redo">↪</ToolbarButton>

      <span className="w-px bg-gray-300 mx-1" />

      <label className="flex items-center gap-1 text-sm px-1 cursor-pointer" title="Text Color">
        A
        <input type="color" className="w-5 h-5 cursor-pointer border-0 p-0 bg-transparent"
          onInput={(e: any) => editor.chain().focus().setColor(e.target.value).run()} />
      </label>
    </div>
  );
}
